import { cn, formatCurrency, formatHours, formatSeconds } from '@/lib/utils'
import {
  CalendarIcon,
  CheckCircle2,
  ChevronDown,
  Clock,
  DollarSign,
  Hourglass,
  Info,
  TrendingUp,
  Users,
  Wallet,
  Layers,
  TrendingDown,
  AlertTriangle,
  Building2,
} from 'lucide-react'
import React, { useState } from 'react'
import { Card, CardContent, CardHeader } from './ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible'
import {
  useGetProjectScheduleQuery,
  useGetSingleProjectQuery,
  type ProjectDetails,
} from '@/redux/service/project'
import { useGetMilestoneWiseProgressQuery } from '@/redux/service/milestone'
import CreateUpdateProject from './CreateUpdateProject'
import ProjectDetailsList from './ProjectDetailsList'
import { Button } from './ui/button'
import dayjs from 'dayjs'
import { Skeleton } from './ui/skeleton'
import { useCheckPermission } from '@/hooks/useCheckPermission'
import Milestones from './Milestones'
import TagWiseBreakdown from './TagWiseBreakdown'
import AdminNotes from './AdminNotes'
import ProjectTeamPerformance from './ProjectTeamPerformance'
import { useLocation } from 'react-router-dom'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip'
import { ProjectSummary } from '@/lib/types'
import ProjectHealthDashboard from './ProjectHealthDashboard'
import ProjectOverviewCards from './ProjectOverviewCards'
import VendorMilestones from './VendorMilestones'
import VendorHoursList from './VendorHoursList'

interface StatCardProps {
  icon: React.ElementType
  label: string
  value: React.ReactNode
  info?: string
  subValue?: string
  className?: string
}

const ProjectViewSkeleton = () => (
  <div className='animate-in fade-in duration-500'>
    <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-8'>
      {[...Array(10)].map((_, i) => (
        <div
          key={i}
          className='flex flex-col gap-2 p-4 rounded-xl bg-secondary/10 border border-border/50'
        >
          <div className='flex items-center gap-2 mb-1'>
            <Skeleton className='w-4 h-4 rounded-full' />
            <Skeleton className='h-3 w-16' />
          </div>
          <Skeleton className='h-7 w-24' />
        </div>
      ))}
    </div>

    <div className='grid md:grid-cols-12 gap-8'>
      <div className='md:col-span-8'>
        <Card className='border-border/40 shadow-sm'>
          <CardHeader className='pb-3'>
            <div className='flex items-center gap-2 mb-4 pb-2 border-b border-border/40'>
              <Skeleton className='w-7 h-7 rounded-full' />
              <Skeleton className='h-4 w-32' />
            </div>
          </CardHeader>
          <CardContent className='space-y-3'>
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className='flex items-center gap-3 p-3 rounded-lg bg-secondary/5 border border-border/40'
              >
                <Skeleton className='w-5 h-5 rounded-full' />
                <div className='flex-1 space-y-2'>
                  <Skeleton className='h-4 w-1/2' />
                  <Skeleton className='h-3 w-1/3' />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className='md:col-span-4'>
        <Card className='border-border/40 shadow-sm h-full'>
          <CardHeader className='pb-3'>
            <div className='flex items-center gap-2 mb-4 pb-2 border-b border-border/40'>
              <Skeleton className='w-7 h-7 rounded-full' />
              <Skeleton className='h-4 w-32' />
            </div>
          </CardHeader>
          <CardContent>
            <div className='flex items-center gap-3 p-3 rounded-lg border border-border/40 bg-card shadow-sm'>
              <Skeleton className='w-10 h-10 rounded-full' />
              <div className='space-y-2'>
                <Skeleton className='h-4 w-24' />
                <Skeleton className='h-3 w-16' />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className='md:col-span-12'>
        <Card className='border-border/40 shadow-sm'>
          <CardHeader className='pb-3'>
            <div className='flex items-center gap-2 mb-4 pb-2 border-b border-border/40'>
              <Skeleton className='w-7 h-7 rounded-full' />
              <Skeleton className='h-4 w-32' />
            </div>
          </CardHeader>
          <CardContent>
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3'>
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className='flex items-center justify-between p-3 rounded-lg bg-secondary/5 border border-border/40'
                >
                  <div className='flex items-center gap-2'>
                    <Skeleton className='w-8 h-8 rounded-full' />
                    <Skeleton className='h-4 w-20' />
                  </div>
                  <Skeleton className='h-5 w-10 rounded-full' />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  </div>
)

const StatCard = ({
  icon: Icon,
  label,
  value,
  info,
  subValue,
  className,
}: StatCardProps) => (
  <div
    className={cn(
      'group relative flex flex-col gap-3 p-5 rounded-2xl bg-card border border-border/60 shadow-sm transition-all duration-300 hover:shadow-md hover:border-primary/20 hover:-translate-y-0.5',
      className
    )}
  >
    <div className='flex items-center justify-between'>
      <div className='flex items-center gap-2.5'>
        <div className='p-2 rounded-lg bg-primary/10 text-primary shadow-sm group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300'>
          <Icon className='w-4 h-4' />
        </div>
        <span className='text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground'>
          {label}
        </span>
      </div>
      {info && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className='p-1 rounded-full hover:bg-muted transition-colors cursor-help'>
                <Info className='w-3.5 h-3.5 text-muted-foreground/60' />
              </div>
            </TooltipTrigger>
            <TooltipContent className='max-w-xs bg-popover text-popover-foreground p-3 rounded-xl border border-border shadow-2xl'>
              <p className='text-[11px] leading-relaxed font-medium'>{info}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>

    <div className='space-y-1.5'>
      <div className='font-bold text-lg text-foreground tracking-tight'>
        {value ?? '—'}
      </div>
      {subValue && (
        <div className='text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70'>
          {subValue}
        </div>
      )}
    </div>

    {/* Subtle accent line on hover */}
    <div className='absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-primary rounded-full transition-all duration-300 group-hover:w-1/3' />
  </div>
)

const SectionHeader = ({
  icon: Icon,
  title,
}: {
  icon: React.ElementType
  title: string
}) => (
  <div className='flex items-center gap-2 mb-4 pb-2 border-b  border-border/40'>
    <div className='p-1.5 rounded-full border'>
      <Icon className='w-4 h-4' />
    </div>
    <h3 className='font-semibold text-sm text-foreground uppercase tracking-wide'>
      {title}
    </h3>
  </div>
)

const CollapsibleSection = ({
  title,
  icon: Icon,
  defaultOpen = true,
  className,
  children,
}: {
  title: string
  icon: React.ElementType
  defaultOpen?: boolean
  className?: string
  children: React.ReactNode
}) => {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={cn('mb-8', className)}
    >
      <CollapsibleTrigger asChild>
        <button
          type='button'
          className='flex items-center gap-2 w-full mb-4 pb-2 border-b border-border/40 text-left group'
        >
          <div className='p-1.5 rounded-full border'>
            <Icon className='w-4 h-4' />
          </div>
          <h3 className='font-semibold text-sm text-foreground uppercase tracking-wide flex-1'>
            {title}
          </h3>
          <ChevronDown
            className={cn(
              'w-4 h-4 text-muted-foreground transition-transform duration-200',
              !open && '-rotate-90'
            )}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>{children}</CollapsibleContent>
    </Collapsible>
  )
}

const MilestoneProgressCard = ({
  listId,
  className,
}: {
  listId: number
  className?: string
}) => {
  const { data, isLoading } = useGetMilestoneWiseProgressQuery(listId, {
    skip: !listId,
  })

  if (isLoading) {
    return (
      <div
        className={cn(
          'rounded-2xl bg-card border border-border/60 shadow-sm p-5 space-y-3',
          className
        )}
      >
        <Skeleton className='h-4 w-36' />
        <Skeleton className='h-2 w-full rounded-full' />
        <div className='flex gap-4'>
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className='h-4 w-16' />
          ))}
        </div>
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className='h-3 w-full rounded-full' />
        ))}
      </div>
    )
  }

  if (!data?.data?.milestones?.length) return null

  const { milestones_summary, milestones } = data.data
  const overallPct = Math.round(milestones_summary.percentage)

  return (
    <div
      className={cn(
        'group relative flex flex-col gap-3 p-5 rounded-2xl bg-card border border-border/60 shadow-sm transition-all duration-300 hover:shadow-md hover:border-primary/20 hover:-translate-y-0.5',
        className
      )}
    >
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2.5'>
          <div className='p-2 rounded-lg bg-primary/10 text-primary shadow-sm group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300'>
            <Layers className='w-4 h-4' />
          </div>
          <span className='text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground'>
            Milestone Progress
          </span>
        </div>
        <span className='text-sm font-bold text-primary'>{overallPct}%</span>
      </div>

      <div className='w-full h-2 rounded-full bg-secondary/40 overflow-hidden'>
        <div
          className='h-full rounded-full bg-primary transition-all duration-700'
          style={{ width: `${overallPct}%` }}
        />
      </div>

      <div className='flex items-center gap-4 text-[11px] font-medium'>
        <span className='flex items-center gap-1.5 text-muted-foreground'>
          <span className='w-2 h-2 rounded-full bg-primary/40 inline-block' />
          Total:{' '}
          <span className='text-foreground font-bold'>
            {milestones_summary.total}
          </span>
        </span>
        <span className='flex items-center gap-1.5 text-muted-foreground'>
          <span className='w-2 h-2 rounded-full bg-emerald-500 inline-block' />
          Done:{' '}
          <span className='text-emerald-600 font-bold'>
            {milestones_summary.completed}
          </span>
        </span>
        <span className='flex items-center gap-1.5 text-muted-foreground'>
          <span className='w-2 h-2 rounded-full bg-orange-400 inline-block' />
          Left:{' '}
          <span className='text-orange-500 font-bold'>
            {milestones_summary.remaining}
          </span>
        </span>
      </div>

      <div className='space-y-2.5 overflow-y-auto max-h-40 pr-0.5'>
        {milestones.map(m => {
          const pct = Math.round(m.tasks.percentage)
          const barColor = m.is_completed
            ? 'bg-emerald-500'
            : m.milestone_status === 'in progress'
              ? 'bg-amber-500'
              : 'bg-border'
          return (
            <div key={m.milestone_id} className='space-y-1'>
              <div className='flex items-center justify-between gap-2'>
                <span className='text-[11px] text-muted-foreground truncate flex-1 min-w-0'>
                  {m.milestone_name}
                </span>
                <div className='flex items-center gap-1 shrink-0'>
                  <span className='text-[10px] font-bold text-foreground'>
                    {pct}%
                  </span>
                  {m.is_completed && (
                    <CheckCircle2 className='w-3 h-3 text-emerald-500' />
                  )}
                </div>
              </div>
              <div className='w-full h-1.5 rounded-full bg-secondary/40 overflow-hidden'>
                <div
                  className={cn(
                    'h-full rounded-full transition-all duration-700',
                    barColor
                  )}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>

      <div className='absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-primary rounded-full transition-all duration-300 group-hover:w-1/3' />
    </div>
  )
}

// const UserAvatar = ({ name }: { name: string }) => (
//   <div className='flex items-center gap-3 p-3 rounded-lg border border-border/40 bg-card shadow-sm'>
//     <div className='w-10 h-10 rounded-full bg-gradient-to-br from-primary to-orange-500 flex items-center justify-center text-primary-foreground font-bold shadow-sm'>
//       {name ? name.charAt(0).toUpperCase() : '?'}
//     </div>
//     <div>
//       <p className='text-sm font-medium leading-none mb-1'>
//         {name ?? 'Unassigned'}
//       </p>
//       <p className='text-xs text-muted-foreground'>Project Lead</p>
//     </div>
//   </div>
// )

const TableCell = ({
  icon: Icon,
  label,
  value,
  subValue,
  className,
  info,
}: {
  icon?: React.ElementType
  label: string
  value: React.ReactNode
  subValue?: React.ReactNode
  className?: string
  info?: string
}) => (
  <div
    className={cn(
      'p-4 px-5 flex items-center justify-between transition-all group hover:bg-secondary/15',
      className
    )}
  >
    <div className='flex items-center gap-3.5 min-w-0'>
      {Icon && (
        <div className='p-2 rounded-lg bg-primary/10 text-primary shadow-sm group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300'>
          <Icon className='w-4 h-4' />
        </div>
      )}
      <div className='flex text-muted-foreground/90 items-center gap-1'>
        <p className='text-sm font-medium  truncate'>{label}</p>
        {info && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className='w-3 h-3 cursor-help' />
              </TooltipTrigger>
              <TooltipContent className='max-w-xs bg-background border  text-popover-foreground p-3 rounded-lg shadow-xl'>
                <p className='text-xs leading-relaxed italic'>{info}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
    <div className='flex items-baseline gap-2 ml-6 shrink-0'>
      <span className='text-sm font-bold text-foreground tracking-tight'>
        {value}
      </span>
      {subValue && (
        <span className='text-[10px] text-muted-foreground/60 font-bold uppercase'>
          {subValue}
        </span>
      )}
    </div>
  </div>
)
const formatPct = (value: string | number | undefined | null) => {
  const num = Number(value)
  if (!Number.isFinite(num)) return '—'
  return `${num.toFixed(2)}%`
}

const SummaryTable = ({
  summary,
  start,
  end,
  dateDone,
  isFullFinance,
  hasVendorWork,
  vendorPaymentType,
}: {
  summary: ProjectSummary | undefined
  start?: string
  end?: string
  dateDone?: string | null
  isFullFinance: boolean
  hasVendorWork?: boolean
  vendorPaymentType?: 'full' | 'milestone' | null | undefined
}) => {
  // Hours only mean something for hourly-paid vendors (payment type null) -
  // full/milestone vendors are paid the committed amount whether or not
  // hours were logged, so hours are hidden for them (backend already
  // zeroes summary.vendor_hours in that case - see mergeVendorCost - this
  // just makes the UI not show a misleading "0h" row at all).
  const isHourlyVendor = hasVendorWork && !vendorPaymentType
  const startDate = start ? dayjs(start) : null
  const dueDate = end ? dayjs(end) : null
  const today = dayjs()
  const getTimelineStatus = () => {
    if (dateDone) {
      return {
        label: 'Completed',
        value: dayjs(dateDone).format('DD MMM, YYYY'),
        color: 'text-emerald-500',
        sub: 'Finished',
      }
    }

    if (!dueDate) return { label: 'Days Left', value: '-', color: '' }

    const diff = dueDate.diff(today, 'day')

    if (startDate && today.isBefore(startDate)) {
      const waitDays = startDate.diff(today, 'day')
      return {
        label: 'Upcoming',
        value: `${waitDays}d`,
        color: 'text-blue-500',
        sub: `Starts ${startDate.format('DD MMM')}`,
      }
    }

    if (diff < 0) {
      return {
        label: 'Overdue By',
        value: `${Math.abs(diff)}d`,
        color: 'text-red-600 font-black animate-pulse',
        sub: 'Action Required',
      }
    }

    if (diff === 0) {
      return {
        label: 'Due Today',
        value: 'Now',
        color: 'text-orange-600 font-black',
        sub: 'Final Deadline',
      }
    }

    return {
      label: 'Days Remaining',
      value: `${diff}d`,
      color: diff <= 3 ? 'text-orange-500' : 'text-emerald-600',
      sub: 'On Track',
    }
  }

  const timeline = getTimelineStatus()

  const totalSpentSeconds =
    (Number(summary?.billable_duration_seconds) || 0) +
    (Number(summary?.nonbillable_duration_seconds) || 0)
  const vendorHours = Number(summary?.vendor_hours) || 0
  const totalSpentHours =
    summary?.total_spent_hours != null
      ? Number(summary.total_spent_hours)
      : totalSpentSeconds / 3600 + vendorHours

  // Financial calculations for safe usage and better design
  const billableCost = Number(summary?.billable_cost) || 0
  const nonBillableCost =
    Number(
      summary?.nonbillable_cost ?? summary?.probono_cost ?? summary?.probon_cost
    ) || 0
  const vendorCost = Number(summary?.vendor_cost) || 0
  const directLabor =
    Number(summary?.total_cost) || billableCost + nonBillableCost
  const overhead = Number(summary?.overhead_cost) || 0
  const fullyLoaded =
    Number(summary?.fully_loaded_cost) || directLabor + overhead
  const billableRevenue = Number(summary?.project_billable_amount) || 0

  const billableProfit = Number(
    summary?.billable_profit ?? billableRevenue - billableCost
  )
  const grossProfit = Number(
    summary?.gross_profit ?? billableRevenue - directLabor
  )
  const netProfit = Number(summary?.net_profit ?? billableRevenue - fullyLoaded)
  const pctOf = (val: number) =>
    billableRevenue > 0 ? (val / billableRevenue) * 100 : 0
  const billableProfitPct = Number(
    summary?.billable_profit_pct ?? pctOf(billableProfit)
  )
  const grossProfitPct = Number(summary?.gross_profit_pct ?? pctOf(grossProfit))
  const netProfitPct = Number(summary?.net_profit_pct ?? pctOf(netProfit))

  const overheadPctOfDirect =
    directLabor > 0 ? Math.round((overhead / directLabor) * 100) : 0

  const profitTone = (val: number) =>
    val < 0 ? 'text-red-600' : 'text-emerald-600'

  const profitValue = (val: number, pct: number) => (
    <span className={cn('font-bold', profitTone(val))}>
      {formatCurrency(val)}
      <span className='ml-2 text-xs font-medium opacity-80'>
        {formatPct(pct)}
      </span>
    </span>
  )

  return (
    <div className='overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm mb-8'>
      {isFullFinance && hasVendorWork && summary?.cost_incomplete && (
        <div className='flex items-center gap-2 px-5 py-2.5 bg-amber-500/10 border-b border-amber-500/20 text-amber-700 dark:text-amber-400'>
          <AlertTriangle className='w-3.5 h-3.5 shrink-0' />
          <p className='text-xs font-medium'>
            Vendor rate/milestone amount not set yet - cost and profit figures
            below are incomplete.
          </p>
        </div>
      )}
      <div
        className={cn(
          'grid grid-cols-1 border-b border-border/60',
          isFullFinance ? 'md:grid-cols-3' : 'md:grid-cols-2'
        )}
      >
        <div className='border-b md:border-b-0 md:border-r border-border/60 bg-muted/20'>
          <TableCell
            icon={Clock}
            label='Total Hours'
            value={formatHours(summary?.total_hours)}
            subValue='HR'
            info='Project estimate in hours.'
          />
          <TableCell
            icon={DollarSign}
            label='Total Budget'
            value={formatCurrency(summary?.total_budget)}
            className='border-t border-border/60'
            info='Project total budget.'
          />
          <TableCell
            icon={DollarSign}
            label='Billed Revenue'
            value={formatCurrency(summary?.project_billable_amount)}
            className='border-t border-border/60 bg-emerald-500/5'
            info='Billed Revenue = hourly rate × billable hours'
          />
        </div>

        {/* Cell 2: Hours Breakdown */}
        <div className='border-b md:border-b-0 md:border-r border-border/60'>
          <TableCell
            icon={Clock}
            label='Billed Hrs'
            value={formatSeconds(summary?.billable_duration_seconds)}
            info='Sum of all employees’ billable hours for this project.'
          />
          <TableCell
            icon={Clock}
            label='Non-Billed'
            value={formatSeconds(summary?.nonbillable_duration_seconds)}
            info='Sum of all employees’ non-billable hours for this project.'
          />
          {isHourlyVendor && (
            <TableCell
              icon={Building2}
              label='Vendor Hours'
              value={formatHours(vendorHours)}
              className='border-t border-border/60 text-orange-600'
              info='Manually logged vendor hours (VENDOR HOURS), additive on top of internal ClickUp hours.'
            />
          )}
          <TableCell
            icon={Hourglass}
            label='Total Spent'
            value={formatHours(totalSpentHours)}
            className='border-t border-border/60 bg-primary/5 text-primary'
            info={
              hasVendorWork
                ? 'Total hours spent: internal employees (billable + non-billable) plus vendor hours.'
                : 'Total hours spent by employees (billable and non-billable)'
            }
          />
        </div>
        {isFullFinance && (
          <div className=''>
            <TableCell
              icon={Wallet}
              label='Billed Cost'
              value={formatCurrency(billableCost)}
              info='Billable cost (Employee hourly rate × billable hours)'
            />
            <TableCell
              icon={Wallet}
              label='Non-Billed Cost'
              value={formatCurrency(nonBillableCost)}
              info='Non-billable cost (Employee hourly rate × non-billable hours)'
            />
            {hasVendorWork && (
              <TableCell
                icon={Building2}
                label='Vendor Cost'
                value={formatCurrency(vendorCost)}
                className={cn(
                  'border-t border-border/60 text-orange-600',
                  summary?.cost_incomplete && 'bg-amber-500/10'
                )}
                info={
                  summary?.cost_incomplete
                    ? 'Vendor cost - the vendor rate/milestone amount has not been set yet, so this (and everything below) understates true cost.'
                    : 'Cost of vendor work: hourly (hours × rate) or the committed fixed/milestone payment amount.'
                }
              />
            )}
            <TableCell
              icon={Wallet}
              label={
                hasVendorWork ? 'Total Direct Cost' : 'Direct Employee Cost'
              }
              value={formatCurrency(directLabor)}
              className='border-t border-border/60 bg-orange-500/5 text-orange-600'
              info={
                hasVendorWork
                  ? 'Direct labor (billable + non-billable) plus vendor cost. Overhead is shown separately below.'
                  : 'Direct labor only (billable + non-billable). Overhead is shown separately below.'
              }
            />
            <TableCell
              icon={TrendingUp}
              label={`Overhead Cost${
                overheadPctOfDirect ? ` (${overheadPctOfDirect}%)` : ''
              }`}
              value={formatCurrency(overhead)}
              className='border-t border-border/60'
              info='Direct labor × expense ratio. Now an explicit line item rather than baked into each cost.'
            />
            <TableCell
              icon={Wallet}
              label='Fully Loaded Cost'
              value={formatCurrency(fullyLoaded)}
              className='border-t border-border/60 bg-orange-500/10 text-orange-700 font-bold'
              info='Direct labor + overhead.'
            />
          </div>
        )}
      </div>

      <div
        className={cn(
          'grid grid-cols-1',
          isFullFinance ? 'md:grid-cols-3' : 'md:grid-cols-2'
        )}
      >
        <div className='border-b md:border-b-0 md:border-r border-border/60 bg-muted/20'>
          <TableCell
            icon={Hourglass}
            label='Remaining Budget Hours'
            value={formatHours(summary?.remaining_hours)}
            subValue='HR'
            info='Remaining Hours (Total Hours − Billable Hours)'
          />
          <TableCell
            icon={Wallet}
            label='Remaining Budget Amount'
            value={formatCurrency(summary?.remaining_billable_amount)}
            className={cn(
              'border-t border-border/60',
              Number(summary?.remaining_billable_amount) < 0 && 'text-red-500'
            )}
            info='Remaining Budget Amount (Total Budget − Billable Project Amount)'
          />
        </div>

        {isFullFinance && (
          <div className='border-b md:border-b-0 md:border-r border-border/60'>
            <TableCell
              icon={billableProfit >= 0 ? TrendingUp : TrendingDown}
              label='Billable Profit (BC)'
              value={profitValue(billableProfit, billableProfitPct)}
              info='Billable revenue − billable direct labor.'
            />
            <TableCell
              icon={grossProfit >= 0 ? TrendingUp : TrendingDown}
              label='Gross Profit (TC)'
              value={profitValue(grossProfit, grossProfitPct)}
              className='border-t border-border/60'
              info='Billable revenue − all direct labor (no overhead).'
            />
            <TableCell
              icon={netProfit >= 0 ? TrendingUp : TrendingDown}
              label='Net Profit (FC)'
              value={profitValue(netProfit, netProfitPct)}
              className={cn(
                'border-t border-border/60 font-bold',
                netProfit < 0 ? 'bg-red-500/[0.04]' : 'bg-emerald-500/[0.04]'
              )}
              info='Billable revenue − fully loaded cost.'
            />
          </div>
        )}

        <div className=''>
          <TableCell
            icon={CalendarIcon}
            label='Start Date'
            value={start ? dayjs(start).format('DD MMM, YYYY') : '-'}
          />
          <TableCell
            icon={CalendarIcon}
            label='Due Date'
            value={end ? dayjs(end).format('DD MMM, YYYY') : '-'}
            className='border-t border-border/60'
          />
          <TableCell
            icon={
              dateDone
                ? CheckCircle2
                : timeline.label === 'Upcoming'
                  ? Clock
                  : Hourglass
            }
            label={timeline.label}
            value={timeline.value}
            subValue={timeline.sub}
            className={cn(
              'border-t border-border/60 font-bold',
              timeline.color
            )}
          />
        </div>
      </div>
    </div>
  )
}
const ProjectView = ({
  project_id,
  list_id,
  closeDialog,
}: {
  readonly project_id: number | undefined
  readonly teamName: string | undefined
  readonly list_id: number
  readonly closeDialog: () => void
}) => {
  const location = useLocation()
  const {
    data: response,
    isLoading,
    isFetching,
  } = useGetSingleProjectQuery(project_id ?? 0, {
    skip: !project_id,
  })
  const { data: scheduleResponse } = useGetProjectScheduleQuery(list_id, {
    skip: !list_id,
  })
  const scheduleData = scheduleResponse?.data
  const { data: milestoneProgressResponse } = useGetMilestoneWiseProgressQuery(
    list_id,
    {
      skip: !list_id,
    }
  )
  const milestoneTotal =
    milestoneProgressResponse?.data?.milestones_summary?.total
  const { hasPageAccess } = useCheckPermission()
  const isAdmin = hasPageAccess('admin_notes')
  const canViewVendorFinance = hasPageAccess('finance')
  const canViewRevenue =
    hasPageAccess('finance') || hasPageAccess('mini_finance')
  const canEditDetails =
    isAdmin || hasPageAccess('finance') || hasPageAccess('mini_finance')
  const [editingDetail, setEditingDetail] =
    React.useState<ProjectDetails | null>(null)
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<
    'performance' | 'milestones' | 'notes' | 'details' | 'vendor'
  >('details')
  if (isLoading || isFetching) {
    return <ProjectViewSkeleton />
  }
  const summary = response?.data?.summary
  const project = response?.data?.project
  // Same "actually linked" check ProjectOverviewCards uses - has_vendor_work
  // can be true mid-setup before a vendor is chosen.
  const hasLinkedVendor =
    !!project?.has_vendor_work && project?.vendor_id != null
  // const milestones = response?.data?.milestones || []

  // const istDate = (dateString: string | null | undefined) => {
  //   if (!dateString) return '—'
  //   return new Date(dateString).toLocaleDateString('en-IN', {
  //     timeZone: 'Asia/Kolkata',
  //     day: 'numeric',
  //     month: 'short',
  //     year: 'numeric',
  //   })
  // }

  const totalSpentSeconds =
    (Number(summary?.billable_duration_seconds) || 0) +
    (Number(summary?.nonbillable_duration_seconds) || 0)
  return (
    <div className='animate-in fade-in duration-500 slide-in-from-bottom-2'>
      {scheduleData && (
        <CollapsibleSection title='Overview' icon={Info}>
          <ProjectOverviewCards
            status={scheduleData.status}
            priority={scheduleData.priority}
            billingType={scheduleData.billing_type}
            communication={scheduleData.communication}
            projectType={scheduleData.project_type}
            techStack={scheduleData.tech_tasks}
            teamName={scheduleData.team}
            department={scheduleData.department}
            deliveryHead={scheduleData.delivery_head}
            teamLeader={scheduleData.assignee_name}
            accountManager={scheduleData.account_manager}
            qaStatus={scheduleData.qa_status}
            milestoneTotal={milestoneTotal}
            timelineHours={
              summary?.total_hours
                ? `${formatHours(summary.total_hours)} Hours`
                : undefined
            }
            startDate={scheduleData.start_date}
            dueDate={scheduleData.due_date}
            dateDone={scheduleData.date_done}
            hasVendorWork={!!response?.data?.project?.has_vendor_work}
            vendorName={response?.data?.project?.vendor_name}
            vendorPaymentType={response?.data?.project?.vendor_payment_type}
            vendorRate={
              response?.data?.project?.vendor_default_rate != null
                ? Number(response.data.project.vendor_default_rate)
                : null
            }
            vendorCost={
              summary?.vendor_cost != null ? Number(summary.vendor_cost) : null
            }
            vendorHours={
              summary?.vendor_hours != null
                ? Number(summary.vendor_hours)
                : null
            }
            billedRevenue={
              summary?.project_billable_amount != null
                ? Number(summary.project_billable_amount)
                : null
            }
            canViewVendorFinance={canViewVendorFinance}
            canViewRevenue={canViewRevenue}
            listId={list_id}
            canEditDates={hasPageAccess('edit_project_dates')}
          />
          <MilestoneProgressCard listId={list_id} />
        </CollapsibleSection>
      )}

      <CollapsibleSection title='Health Dashboard' icon={TrendingUp}>
        <ProjectHealthDashboard
          summary={summary}
          startDate={scheduleData?.start_date}
          dueDate={scheduleData?.due_date}
          dateDone={scheduleData?.date_done}
          hasVendorWork={!!project?.has_vendor_work}
          financeAccess={
            hasPageAccess('finance')
              ? 'full'
              : hasPageAccess('mini_finance')
                ? 'mini'
                : 'none'
          }
        />
      </CollapsibleSection>

      <CollapsibleSection title='Financial Summary' icon={Wallet}>
        {hasPageAccess('finance') || hasPageAccess('mini_finance') ? (
          <SummaryTable
            summary={summary}
            start={scheduleData?.start_date ?? ''}
            end={scheduleData?.due_date ?? ''}
            dateDone={scheduleData?.date_done ?? ''}
            isFullFinance={hasPageAccess('finance')}
            hasVendorWork={!!response?.data?.project?.has_vendor_work}
            vendorPaymentType={response?.data?.project?.vendor_payment_type}
          />
        ) : (
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
            <StatCard
              icon={Clock}
              label='Total Hours'
              info='The total number of hours logged for this project.'
              value={
                summary?.total_hours ? (
                  <span className='flex items-center gap-1'>
                    {formatHours(summary.total_hours)}
                    <span className='text-xs font-normal text-muted-foreground'>
                      hrs
                    </span>
                  </span>
                ) : (
                  '-'
                )
              }
            />
            <StatCard
              icon={Clock}
              label='Billable Hours'
              info='Time tracked that can be billed to the client.'
              value={formatSeconds(summary?.billable_duration_seconds)}
            />
            <StatCard
              icon={Clock}
              label='Non-Billable'
              info='Time tracked that is not billable.'
              value={formatSeconds(summary?.nonbillable_duration_seconds)}
            />
            <StatCard
              icon={Hourglass}
              label='Total Spent'
              info='Sum of billable and non-billable hours.'
              value={formatSeconds(totalSpentSeconds)}
            />
          </div>
        )}
      </CollapsibleSection>

      <div className='grid md:grid-cols-12 gap-8'>
        <div className='flex w-full'>
          <div className='flex p-1  bg-muted border border-border rounded-xl gap-1 '>
            {(
              [
                { key: 'details' as const, label: 'Project Details' },
                ...(isAdmin
                  ? [{ key: 'notes' as const, label: 'Admin Notes' }]
                  : []),
                ...(location.pathname === '/'
                  ? [
                      {
                        key: 'milestones' as const,
                        label: 'Project Milestones',
                      },
                      {
                        key: 'performance' as const,
                        label: 'Project Team Performance',
                      },
                    ]
                  : []),
                ...(hasLinkedVendor
                  ? [
                      {
                        key: 'vendor' as const,
                        label: project?.vendor_payment_type
                          ? 'Vendor Milestones'
                          : 'Vendor Hours',
                      },
                    ]
                  : []),
              ] as const
            ).map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-4 py-1.5 text-xs font-medium transition-all duration-200 rounded-lg whitespace-nowrap ${
                  activeTab === key
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'details' && (
          <div className='md:col-span-12'>
            <Card className='border-border/40 shadow-sm'>
              <CardContent className='pt-6'>
                <ProjectDetailsList
                  projectId={project_id ?? 0}
                  showActions={canEditDetails}
                  {...(canEditDetails && {
                    onEdit: (d: ProjectDetails) => {
                      setEditingDetail(d)
                      setDetailsDrawerOpen(true)
                    },
                  })}
                />
              </CardContent>
            </Card>
          </div>
        )}
        {activeTab === 'notes' && isAdmin && project_id && (
          <div className='md:col-span-12'>
            <AdminNotes projectId={project_id} />
          </div>
        )}
        {activeTab === 'vendor' && hasLinkedVendor && project?.vendor_id && (
          <div className='md:col-span-12 space-y-6'>
            {project.vendor_payment_type ? (
              <VendorMilestones
                projectId={project_id ?? 0}
                vendorName={project.vendor_name ?? 'Unknown Vendor'}
                paymentType={project.vendor_payment_type}
              />
            ) : (
              <>
                <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
                  <StatCard
                    icon={DollarSign}
                    label="We're Getting"
                    value={
                      canViewRevenue
                        ? formatCurrency(summary?.project_billable_amount ?? 0)
                        : '—'
                    }
                    info='Billed revenue from the client for this project.'
                  />
                  <StatCard
                    icon={Building2}
                    label='Given to Vendor'
                    value={
                      canViewVendorFinance
                        ? formatCurrency(summary?.vendor_cost ?? 0)
                        : '—'
                    }
                    info='Cost of vendor work: hourly (hours × rate) or the committed fixed/milestone amount.'
                  />
                  {canViewRevenue && canViewVendorFinance && (
                    <StatCard
                      icon={
                        Number(summary?.project_billable_amount ?? 0) -
                          Number(summary?.vendor_cost ?? 0) >=
                        0
                          ? TrendingUp
                          : TrendingDown
                      }
                      label='Margin'
                      value={
                        <span
                          className={
                            Number(summary?.project_billable_amount ?? 0) -
                              Number(summary?.vendor_cost ?? 0) >=
                            0
                              ? 'text-emerald-600'
                              : 'text-red-600'
                          }
                        >
                          {formatCurrency(
                            Number(summary?.project_billable_amount ?? 0) -
                              Number(summary?.vendor_cost ?? 0)
                          )}
                        </span>
                      }
                      info="What we're getting from the client minus what we're giving the vendor."
                    />
                  )}
                </div>

                <Card className='border-border/40 shadow-sm'>
                  <CardContent className='pt-6'>
                    <VendorHoursList
                      projectId={project_id ?? 0}
                      vendorId={project.vendor_id}
                      vendorName={project.vendor_name ?? 'Unknown Vendor'}
                      {...(project.vendor_default_rate != null
                        ? {
                            vendorDefaultRate: Number(
                              project.vendor_default_rate
                            ),
                          }
                        : {})}
                    />
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}
        {activeTab === 'milestones' && location.pathname === '/' && (
          <>
            <div className='md:col-span-12'>
              <Card className='border-border/40 shadow-sm'>
                <CardContent className='pt-6'>
                  <Milestones projectId={list_id ?? 0} />
                </CardContent>
              </Card>
            </div>
            <div className='md:col-span-12'>
              <Card className='border-border/40 shadow-sm'>
                <CardContent className='pt-6'>
                  <TagWiseBreakdown listId={list_id ?? 0} />
                </CardContent>
              </Card>
            </div>
          </>
        )}
        {activeTab === 'performance' && (
          <div className='md:col-span-12'>
            <Card className='border-border/40 shadow-sm overflow-hidden'>
              <CardHeader className='pb-3'>
                <SectionHeader icon={Users} title='Project Team Performance' />
              </CardHeader>
              <CardContent>
                <ProjectTeamPerformance projectId={list_id ?? 0} />
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <div className='flex justify-end mt-8 pt-4 border-t border-border/40'>
        <Button variant='outline' onClick={closeDialog}>
          Close View
        </Button>
      </div>

      {canEditDetails && (
        <CreateUpdateProject
          open={detailsDrawerOpen}
          onClose={() => {
            setDetailsDrawerOpen(false)
            setEditingDetail(null)
          }}
          project_id={project_id}
          list_id={list_id}
          detail={editingDetail}
          project_type={scheduleData?.project_type}
        />
      )}
    </div>
  )
}

export default ProjectView
